import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { FileSpreadsheet, FileText, AlertTriangle, Boxes } from "lucide-react";
import ModalShell from "./ModalShell";

/**
 * Artikel-Import — zwei Modi:
 *   • CSV-Import (Standard-Massenpflege)
 *   • König-Teilebestand (Kommissionsware) — liest die „Gesamtliste Ersatzteile"
 *     von Autohaus Gotthard König GmbH (.xls/.xlsx/.txt) und markiert die Teile
 *     auf Wunsch als Kommissionsware inkl. automatischer Lieferanten-Anlage.
 */
export default function ImportModal({ onClose, onDone }) {
    const [mode, setMode] = useState("csv"); // "csv" | "koenig" | "piaggio"
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [action, setAction] = useState("upsert");
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState(null); // {processed,total,...} für Piaggio-Job
    // König-Optionen
    const [consignment, setConsignment] = useState(true);
    const [supplier, setSupplier] = useState("Autohaus Gotthard König GmbH");
    const [genEan8, setGenEan8] = useState(true);
    // Jun-2026 (User-Wunsch) — Zielstandort wählen (Hauptsitz kann in eine Filiale importieren).
    const [locations, setLocations] = useState([]);
    const [targetDealer, setTargetDealer] = useState("");
    const fileRef = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/org/me");
                const opts = [];
                if (data?.self) opts.push({ id: data.self.id, label: `${data.self.site_code || "HQ"} — ${data.self.company_name || "Hauptsitz"} (eigener Standort)` });
                (data?.branches || []).forEach((b) => opts.push({ id: b.id, label: `${b.site_code || ""} — ${b.company_name || ""}`.trim() }));
                setLocations(opts);
                if (data?.self) setTargetDealer(data.self.id);
            } catch (_e) { /* Einzel-Standort: keine Auswahl nötig */ }
        })();
    }, []);

    const isKoenig = mode === "koenig";
    const isPiaggio = mode === "piaggio";
    const isGovecs = mode === "govecs";

    const switchMode = (m) => {
        setMode(m); setFile(null); setPreview(null); setProgress(null);
        if (fileRef.current) fileRef.current.value = "";
    };

    const doPreview = async () => {
        if (!file) { toast.error("Bitte Datei wählen"); return; }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            let url = "/articles/import/preview";
            if (isKoenig) url = "/articles/import/koenig/preview";
            else if (isPiaggio) { url = "/articles/import/piaggio/preview"; fd.append("vat_rate", "19"); }
            else if (isGovecs) url = "/articles/import/govecs/preview";
            const res = await api.post(url, fd);
            setPreview(res.data);
        } catch (e) { toast.error(e.response?.data?.detail || "Vorschau fehlgeschlagen"); }
        setBusy(false);
    };

    const pollPiaggio = async (jobId) => {
        try {
            const { data } = await api.get(`/articles/import/piaggio/status/${jobId}`);
            setProgress(data);
            if (data.status === "done") {
                toast.success(`Piaggio-Import fertig · ${data.inserted} neu · ${data.updated} aktualisiert · ${data.ean8_generated} EAN-8 erzeugt`);
                setBusy(false);
                onDone();
                return;
            }
        } catch { /* weiter versuchen */ }
        setTimeout(() => pollPiaggio(jobId), 1500);
    };

    const commit = async () => {
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            if (isPiaggio) {
                fd.append("vat_rate", "19");
                fd.append("generate_ean8", genEan8 ? "true" : "false");
                fd.append("supplier_name", "Piaggio");
                const res = await api.post("/articles/import/piaggio/commit", fd);
                setProgress({ processed: 0, total: res.data.total, status: "running" });
                toast.message(`Import gestartet: ${res.data.total} Teile werden im Hintergrund verarbeitet…`);
                pollPiaggio(res.data.job_id);
                return; // busy bleibt bis Job fertig
            }
            if (isGovecs) {
                fd.append("generate_ean8", genEan8 ? "true" : "false");
                fd.append("supplier_name", "Govecs");
                const res = await api.post("/articles/import/govecs/commit", fd);
                toast.success(`Govecs-Import fertig · ${res.data.inserted} neu · ${res.data.updated} aktualisiert · ${res.data.ean8_generated} EAN-8 erzeugt · Lieferant: ${res.data.supplier?.name || "Govecs"}`);
                onDone();
                setBusy(false);
                return;
            }
            fd.append("action", action);
            let url = "/articles/import/commit";
            if (isKoenig) {
                url = "/articles/import/koenig/commit";
                fd.append("ownership", consignment ? "consignment" : "own");
                fd.append("consignment_supplier", supplier);
                fd.append("vat_rate", "19");
                fd.append("generate_ean8", genEan8 ? "true" : "false");
                if (targetDealer) fd.append("target_dealer_id", targetDealer);
            }
            const res = await api.post(url, fd);
            const extra = res.data.supplier ? ` · Lieferant: ${res.data.supplier.name}` : "";
            const eanInfo = res.data.ean8_generated ? ` · ${res.data.ean8_generated} EAN-8 erzeugt` : "";
            toast.success(`${res.data.inserted} neu · ${res.data.updated} aktualisiert · ${res.data.skipped} übersprungen${eanInfo}${extra}`);
            onDone();
        } catch (e) { toast.error(e.response?.data?.detail || "Import fehlgeschlagen"); }
        setBusy(false);
    };

    return (
        <ModalShell onClose={onClose} title="Artikel-Import" wide testid="import-modal">
            <div className="space-y-4">
                {/* Modus-Umschalter */}
                <div className="grid grid-cols-2 gap-2" data-testid="import-mode-switch">
                    <button
                        type="button"
                        onClick={() => switchMode("csv")}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-sm border-2 text-sm font-bold transition-colors ${mode === "csv" ? "border-[#111827] bg-[#111827] text-white" : "border-gray-200 text-gray-600 hover:border-gray-400"}`}
                        data-testid="import-mode-csv"
                    >
                        <FileText className="w-4 h-4" /> CSV-Import
                    </button>
                    <button
                        type="button"
                        onClick={() => switchMode("koenig")}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-sm border-2 text-sm font-bold transition-colors ${mode === "koenig" ? "border-[#111827] bg-[#111827] text-white" : "border-gray-200 text-gray-600 hover:border-gray-400"}`}
                        data-testid="import-mode-koenig"
                    >
                        <FileSpreadsheet className="w-4 h-4" /> König-Bestand
                    </button>
                    <button
                        type="button"
                        onClick={() => switchMode("piaggio")}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-sm border-2 text-sm font-bold transition-colors ${mode === "piaggio" ? "border-[#111827] bg-[#111827] text-white" : "border-gray-200 text-gray-600 hover:border-gray-400"}`}
                        data-testid="import-mode-piaggio"
                    >
                        <FileSpreadsheet className="w-4 h-4" /> Piaggio-Preisliste
                    </button>
                    <button
                        type="button"
                        onClick={() => switchMode("govecs")}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-sm border-2 text-sm font-bold transition-colors ${mode === "govecs" ? "border-[#111827] bg-[#111827] text-white" : "border-gray-200 text-gray-600 hover:border-gray-400"}`}
                        data-testid="import-mode-govecs"
                    >
                        <FileSpreadsheet className="w-4 h-4" /> Govecs-Preisliste
                    </button>
                </div>

                {isKoenig ? (
                    <div className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded-sm text-xs text-amber-900">
                        <div className="font-bold mb-1 flex items-center gap-1.5"><Boxes className="w-4 h-4" /> König-Teilebestand (Gesamtliste Ersatzteile)</div>
                        Lädt den König-Report (<code>.xls / .xlsx / .txt</code>) direkt. Spalten (Teilenummer, Beschreibung, VK-Preis, UPE, Dur.EK-Pr, Lagerort, Bestand) werden automatisch erkannt. VK-Preis = Brutto (19 % MwSt), UPE = UVP, Dur.EK-Pr = EK netto. Negativbestand wird übernommen. Liefert König künftig <b>EAN</b> oder <b>Hersteller-Teilenummer</b>, werden diese automatisch übernommen (Barcode-Scan im Auftrag möglich).
                    </div>
                ) : isPiaggio ? (
                    <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded-sm text-xs text-red-900">
                        <div className="font-bold mb-1 flex items-center gap-1.5"><Boxes className="w-4 h-4" /> Piaggio-Ersatzteil-Preisliste (.xlsx)</div>
                        Spalten (Rabattgruppe, Marke, Teilenummer, Beschreibung, UVP netto) werden per Kopfzeile erkannt. EK netto = UVP × (1 − Rabattgruppen-Rabatt), VK netto = UVP × 1,3. Import läuft im Hintergrund, Bestände bleiben erhalten.
                    </div>
                ) : isGovecs ? (
                    <div className="bg-green-50 border-l-4 border-green-600 p-3 rounded-sm text-xs text-green-900">
                        <div className="font-bold mb-1 flex items-center gap-1.5"><Boxes className="w-4 h-4" /> Govecs e-Schwalbe Preisliste (.xlsx)</div>
                        Jeder Tab = ein Modell (BJ ab 2023 → <b>ES23</b>, refurbed → <b>ESRF</b>, original 2017–2022 → <b>ES17</b>). Es werden automatisch <b>sprechende Artikelnummern</b> erzeugt, z. B. <code>GOV-ES23-SCHUTZBLECH-VO</code>. Netto-UVP → VK netto, Brutto (19 %) → VK brutto &amp; UVP. EK bleibt leer (später pflegen), Bestand startet bei 0. Lieferant „Govecs" wird automatisch angelegt.
                    </div>
                ) : (
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded-sm">
                        <div className="text-xs text-blue-900">
                            <div className="font-bold mb-1">Erwartete Spalten (Semikolon oder Komma):</div>
                            <code className="text-[10px] block bg-white/50 p-2 rounded-sm font-mono-num">sku;name;category;manufacturer;manufacturer_part_number;ean;purchase_price_net;sell_price_gross;vat_rate;stock_qty;min_stock;unit;location</code>
                            <div className="mt-2">Deutsche Zahlenformate (1.234,56) werden automatisch erkannt. Kategorien: part / tire / accessory / fluid / labour / insurance_plate / sales / other.</div>
                        </div>
                    </div>
                )}

                <input
                    ref={fileRef}
                    type="file"
                    accept={isKoenig ? ".xls,.xlsx,.txt,.csv" : (isPiaggio || isGovecs) ? ".xlsx" : ".csv,.txt"}
                    onChange={(e) => { setFile(e.target.files[0]); setPreview(null); }}
                    className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded-sm file:border-0 file:text-xs file:font-bold file:bg-[#111827] file:text-white hover:file:bg-black"
                    data-testid="import-file"
                />

                {/* König-Optionen */}
                {isKoenig && (
                    <div className="space-y-3 border border-gray-200 rounded-sm p-3 bg-gray-50">
                        {locations.length > 1 && (
                            <div data-testid="import-koenig-location-block">
                                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Zielstandort</label>
                                <Select value={targetDealer} onValueChange={setTargetDealer}>
                                    <SelectTrigger className="rounded-sm mt-1" data-testid="import-koenig-location"><SelectValue placeholder="Standort wählen" /></SelectTrigger>
                                    <SelectContent>
                                        {locations.map((l) => (
                                            <SelectItem key={l.id} value={l.id} data-testid={`import-location-${l.id}`}>{l.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <div className="text-[10px] text-gray-500 mt-1">Die Teile werden diesem Standort als Bestand zugeordnet.</div>
                            </div>
                        )}
                        <label className="flex items-center gap-2 text-sm font-bold text-gray-800 cursor-pointer">
                            <input type="checkbox" checked={consignment} onChange={(e) => setConsignment(e.target.checked)} className="w-4 h-4 accent-[#111827]" data-testid="import-koenig-consignment" />
                            Als Kommissionsware markieren (zählt nicht zum Eigen-Lagerwert)
                        </label>
                        {consignment && (
                            <div>
                                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Lieferant (Kommission)</label>
                                <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} className="rounded-sm mt-1 text-sm" data-testid="import-koenig-supplier" />
                                <div className="text-[10px] text-gray-500 mt-1">Wird automatisch als Lieferant angelegt, falls noch nicht vorhanden.</div>
                            </div>
                        )}
                        <label className="flex items-center gap-2 text-sm font-bold text-gray-800 cursor-pointer">
                            <input type="checkbox" checked={genEan8} onChange={(e) => setGenEan8(e.target.checked)} className="w-4 h-4 accent-[#111827]" data-testid="import-koenig-genean8" />
                            EAN-8 (Haus-Barcode) für Teile ohne EAN erzeugen
                        </label>
                        {genEan8 && (
                            <div className="text-[10px] text-gray-500 -mt-1">Erzeugt einen internen, scanbaren EAN-8 aus der Artikelnummer (kein offizieller GTIN). Vorhandene/gelieferte EANs bleiben unverändert.</div>
                        )}
                    </div>
                )}

                {/* Govecs-Optionen */}
                {isGovecs && (
                    <div className="border border-gray-200 rounded-sm p-3 bg-gray-50">
                        <label className="flex items-center gap-2 text-sm font-bold text-gray-800 cursor-pointer">
                            <input type="checkbox" checked={genEan8} onChange={(e) => setGenEan8(e.target.checked)} className="w-4 h-4 accent-[#111827]" data-testid="import-govecs-genean8" />
                            EAN-8 (Haus-Barcode) für Artikel ohne EAN erzeugen
                        </label>
                    </div>
                )}

                {!preview ? (
                    <Button onClick={doPreview} disabled={!file || busy} className="w-full rounded-sm bg-[#111827] hover:bg-black text-white font-bold" data-testid="import-preview-btn">
                        {busy ? "Lese Datei…" : "Vorschau prüfen"}
                    </Button>
                ) : (
                    <div className="space-y-3">
                        <div className="grid grid-cols-3 gap-3">
                            <div className="bg-green-50 border border-green-200 rounded-sm p-3">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-green-700">Neu</div>
                                <div className="font-display font-black text-2xl text-green-900" data-testid="import-stat-new">{preview.new_skus_count}</div>
                            </div>
                            <div className="bg-blue-50 border border-blue-200 rounded-sm p-3">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-blue-700">Aktualisiert</div>
                                <div className="font-display font-black text-2xl text-blue-900" data-testid="import-stat-updated">{preview.updated_skus_count}</div>
                            </div>
                            <div className={`${(isKoenig || isGovecs || isPiaggio) ? "bg-amber-50 border-amber-200" : "bg-red-50 border-red-200"} border rounded-sm p-3`}>
                                <div className={`text-[10px] font-bold uppercase tracking-wider ${(isKoenig || isGovecs || isPiaggio) ? "text-amber-700" : "text-red-700"}`}>{(isKoenig || isGovecs || isPiaggio) ? "Hinweise" : "Fehler"}</div>
                                <div className={`font-display font-black text-2xl ${(isKoenig || isGovecs || isPiaggio) ? "text-amber-900" : "text-red-900"}`} data-testid="import-stat-issues">{(isKoenig || isGovecs || isPiaggio) ? (preview.warnings_count || 0) : preview.invalid_rows}</div>
                            </div>
                        </div>

                        {isKoenig && (
                            <div className="text-xs text-gray-600 flex items-center gap-3 flex-wrap" data-testid="import-koenig-meta">
                                <span><b>{preview.total_rows}</b> Teile erkannt</span>
                                {preview.lager && <span>· Lager: <b>{preview.lager}</b></span>}
                                {consignment && <span>· als Kommissionsware von <b>{supplier}</b></span>}
                                {preview.detected_optional_columns?.length > 0 && (
                                    <span className="text-green-700" data-testid="import-koenig-optional-cols">
                                        · erkannt: <b>{preview.detected_optional_columns.map((c) => c === "ean" ? "EAN" : "Hersteller-Teilenummer").join(", ")}</b> (Barcode-Scan möglich)
                                    </span>
                                )}
                            </div>
                        )}

                        {isGovecs && (
                            <div className="text-xs text-gray-600 flex items-center gap-3 flex-wrap" data-testid="import-govecs-meta">
                                <span><b>{preview.total_rows}</b> Artikel erkannt</span>
                                {preview.model_counts && Object.entries(preview.model_counts).map(([m, c]) => (
                                    <span key={m}>· {m}: <b>{c}</b></span>
                                ))}
                            </div>
                        )}

                        {isGovecs && preview.sample?.length > 0 && (
                            <div className="border border-gray-200 rounded-sm overflow-hidden" data-testid="import-govecs-sample">
                                <div className="bg-gray-100 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-gray-600">Beispiel: erzeugte Artikelnummern</div>
                                <div className="max-h-40 overflow-y-auto">
                                    {preview.sample.map((s, i) => (
                                        <div key={i} className="flex items-center justify-between gap-2 px-2 py-1 text-[11px] border-t border-gray-100">
                                            <code className="font-mono-num font-bold text-gray-900">{s.sku}</code>
                                            <span className="text-gray-600 truncate flex-1">{s.name}</span>
                                            <span className="text-gray-900 font-bold whitespace-nowrap">{Number(s.sell_price_gross).toFixed(2)} € br.</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {isGovecs && preview.warnings?.length > 0 && (
                            <div className="bg-amber-50 border border-amber-200 rounded-sm p-2 max-h-24 overflow-y-auto text-[11px] text-amber-900">
                                {preview.warnings.slice(0, 10).map((w, i) => (
                                    <div key={i} className="flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {w.warn}</div>
                                ))}
                            </div>
                        )}

                        {isKoenig && preview.warnings?.length > 0 && (
                            <div className="bg-amber-50 border border-amber-200 rounded-sm p-2 max-h-24 overflow-y-auto text-[11px] text-amber-900">
                                {preview.warnings.slice(0, 10).map((w, i) => (
                                    <div key={i} className="flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {w.sku}: {w.warn}</div>
                                ))}
                            </div>
                        )}
                        {!isKoenig && preview.errors?.length > 0 && (
                            <div className="bg-red-50 border border-red-200 rounded-sm p-2 max-h-24 overflow-y-auto text-[11px] text-red-900">
                                {preview.errors.slice(0, 10).map((e, i) => (
                                    <div key={i}>Zeile {e.row}: {e.error}</div>
                                ))}
                            </div>
                        )}

                        {!isGovecs && !isPiaggio && (
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Aktion</label>
                            <Select value={action} onValueChange={setAction}>
                                <SelectTrigger className="rounded-sm mt-1" data-testid="import-action"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="upsert">Anlegen + Aktualisieren (Upsert)</SelectItem>
                                    <SelectItem value="create">Nur neue anlegen (vorhandene überspringen)</SelectItem>
                                    <SelectItem value="update">Nur vorhandene aktualisieren</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        )}
                        <Button onClick={commit} disabled={busy} className="w-full rounded-sm bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black uppercase tracking-wider" data-testid="import-commit-btn">
                            {busy ? "Importiere…" : "Import bestätigen"}
                        </Button>
                    </div>
                )}
            </div>
        </ModalShell>
    );
}
